/**
 * ScheduleReleaseStep1
 * Step 1: Select the period for schedule release
 *
 * @version 2025.1.0
 */
import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Paper,
  Alert,
} from '@mui/material';
import { DateRange } from '@mui/icons-material';

interface ScheduleReleaseStep1Props {
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
}

const ScheduleReleaseStep1: React.FC<ScheduleReleaseStep1Props> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
}) => {
  // Validate date range
  const isValidRange = startDate && endDate && startDate <= endDate;
  const daysDiff = isValidRange
    ? Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1
    : 0;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Selecione o Período
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Defina o período para o qual deseja liberar a agenda do profissional.
        Os slots serão gerados automaticamente com base na configuração da agenda.
      </Typography>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <DateRange color="primary" />
          <Typography variant="subtitle1">Período de Liberação</Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <TextField
            label="Data Inicial"
            type="date"
            value={startDate}
            onChange={(e) => onStartDateChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 200 }}
          />

          <TextField
            label="Data Final"
            type="date"
            value={endDate}
            onChange={(e) => onEndDateChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 200 }}
          />
        </Box>

        {isValidRange && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Período selecionado: <strong>{daysDiff} dias</strong>
          </Typography>
        )}
      </Paper>

      <Alert severity="info">
        <Typography variant="body2">
          <strong>Dica:</strong> Os feriados dentro do período serão identificados automaticamente
          e você poderá decidir se deseja trabalhar em cada um deles no próximo passo.
        </Typography>
      </Alert>

      {!isValidRange && startDate && endDate && (
        <Alert severity="error" sx={{ mt: 2 }}>
          A data inicial deve ser anterior ou igual a data final.
        </Alert>
      )}
    </Box>
  );
};

export default ScheduleReleaseStep1;
